import { localAddresses as chainLocalAddresses } from '@endure/chains';
import { type Address, getAddress, isAddress } from 'viem';

import deployAddresses from '../../../deploy/addresses.json';

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

export const requiredLocalAddressKeys = [
  'unitroller',
  'comptrollerLens',
  'accessControlManager',
  'resilientOracle',
  'marketFacet',
  'policyFacet',
  'setterFacet',
  'rewardFacet',
  'vWTAO',
  'vAlpha30',
  'vAlpha64',
  'irmWTAO',
  'irmAlpha',
  'wtao',
  'mockAlpha30',
  'mockAlpha64',
] as const;

export type LocalAddressKey = (typeof requiredLocalAddressKeys)[number];

export type LocalAddresses = Record<LocalAddressKey, Address>;

const parseLocalAddress = (key: LocalAddressKey): Address => {
  const address = deployAddresses[key];

  if (typeof address !== 'string' || !isAddress(address) || getAddress(address) === ZERO_ADDRESS) {
    throw new Error(`Invalid local deploy address for ${key}`);
  }

  return getAddress(address);
};

export const getLocalDeployAddresses = () =>
  requiredLocalAddressKeys.reduce<LocalAddresses>((acc, key) => {
    acc[key] = parseLocalAddress(key);

    return acc;
  }, {} as LocalAddresses);

export const assertLocalDeployAddressesAreUsable = () => {
  const localDeployAddresses = getLocalDeployAddresses();
  const staleAddressKeys = requiredLocalAddressKeys.filter((key) => {
    const chainAddress = (chainLocalAddresses as Record<LocalAddressKey, Address>)[key];

    return getAddress(chainAddress) !== localDeployAddresses[key];
  });

  if (staleAddressKeys.length > 0) {
    throw new Error(
      `Local address metadata is stale for: ${staleAddressKeys.join(', ')}. Refresh @endure/chains localAddresses from packages/deploy/addresses.json.`,
    );
  }
};
